/**
 * Record page comment thread: fetches and renders the thread, and wires up
 * posting, replying to, and deleting comments against the
 * open_vis_framework.comments REST API.
 *
 * Plain DOM APIs throughout (textContent, not innerHTML) for anything that
 * holds user-submitted text, so a comment's own content can never be
 * interpreted as markup.
 */

function getCsrfToken() {
  const match = document.cookie.match(/(?:^|; )csrftoken=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function apiRequest(url, options = {}) {
  const headers = Object.assign({ Accept: "application/json" }, options.headers || {});
  if (options.method && options.method !== "GET") {
    headers["Content-Type"] = "application/json";
    const token = getCsrfToken();
    if (token) headers["X-CSRFToken"] = token;
  }
  const response = await fetch(url, {
    credentials: "same-origin",
    ...options,
    headers,
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const message = payload && payload.message ? payload.message : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return payload;
}

function formatTimestamp(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function initComments() {
  const container = document.getElementById("ovf-comments-thread");
  if (!container) return;

  const apiBase = container.dataset.apiBase;
  const labels = container.dataset;
  const isAuthenticated = container.dataset.isAuthenticated === "true";
  const currentUserId = container.dataset.currentUserId;
  const form = document.getElementById("ovf-comment-form");
  const formStatus = form ? form.querySelector(".ovf-comment-form-status") : null;

  function renderComment(comment) {
    const article = document.createElement("article");
    article.className = "ovf-comment";
    article.dataset.commentId = comment.id;

    const meta = document.createElement("p");
    meta.className = "ovf-comment-meta";
    const author = document.createElement("strong");
    author.textContent = comment.user;
    meta.appendChild(author);
    meta.appendChild(document.createTextNode(" · " + formatTimestamp(comment.created)));
    article.appendChild(meta);

    const body = document.createElement("p");
    body.className = "ovf-comment-content";
    body.textContent = comment.content;
    article.appendChild(body);

    if (isAuthenticated) {
      const actions = document.createElement("p");
      actions.className = "ovf-comment-actions";

      if (!comment.is_hidden) {
        const replyButton = document.createElement("button");
        replyButton.type = "button";
        replyButton.className = "ui mini basic button ovf-comment-reply";
        replyButton.textContent = labels.replyLabel;
        replyButton.addEventListener("click", () => toggleReplyForm(article, comment.id));
        actions.appendChild(replyButton);
      }

      if (!comment.is_hidden && String(comment.user_id) === String(currentUserId)) {
        const deleteButton = document.createElement("button");
        deleteButton.type = "button";
        deleteButton.className = "ui mini basic negative button ovf-comment-delete";
        deleteButton.textContent = labels.deleteLabel;
        deleteButton.addEventListener("click", () => handleDelete(comment.id));
        actions.appendChild(deleteButton);
      }

      if (actions.childNodes.length) article.appendChild(actions);
    }

    if (comment.replies && comment.replies.length) {
      const replies = document.createElement("div");
      replies.className = "ovf-comment-replies";
      comment.replies.forEach((reply) => replies.appendChild(renderComment(reply)));
      article.appendChild(replies);
    }

    return article;
  }

  function toggleReplyForm(article, parentId) {
    const existing = article.querySelector(":scope > .ovf-reply-form");
    if (existing) {
      existing.remove();
      return;
    }
    const replyForm = document.createElement("form");
    replyForm.className = "ovf-reply-form";

    const textarea = document.createElement("textarea");
    textarea.rows = 2;
    textarea.required = true;
    replyForm.appendChild(textarea);

    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "ui mini primary button";
    submit.textContent = labels.postReplyLabel;
    replyForm.appendChild(submit);

    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.className = "ui mini basic button";
    cancel.textContent = labels.cancelLabel;
    cancel.addEventListener("click", () => replyForm.remove());
    replyForm.appendChild(cancel);

    const status = document.createElement("p");
    status.className = "ovf-reply-form-status";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    replyForm.appendChild(status);

    replyForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const content = textarea.value.trim();
      if (!content) return;
      try {
        await apiRequest(`${apiBase}/${parentId}/reply`, {
          method: "POST",
          body: JSON.stringify({ content }),
        });
        await loadThread();
      } catch (error) {
        status.textContent = error.message || labels.errorLabel;
      }
    });

    article.appendChild(replyForm);
    textarea.focus();
  }

  async function handleDelete(commentId) {
    if (!window.confirm(labels.confirmDeleteLabel)) return;
    try {
      await apiRequest(`${apiBase}/${commentId}`, { method: "DELETE" });
      await loadThread();
    } catch (error) {
      window.alert(error.message || labels.errorLabel);
    }
  }

  async function loadThread() {
    container.replaceChildren();
    const status = document.createElement("p");
    status.className = "ovf-comments-status";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    status.textContent = labels.loadingLabel;
    container.appendChild(status);

    try {
      const payload = await apiRequest(apiBase, { method: "GET" });
      const thread = (payload && payload.hits) || [];
      container.replaceChildren();
      if (!thread.length) {
        const empty = document.createElement("p");
        empty.className = "ovf-comments-status";
        empty.textContent = labels.emptyLabel;
        container.appendChild(empty);
        return;
      }
      thread.forEach((comment) => container.appendChild(renderComment(comment)));
    } catch (error) {
      container.replaceChildren();
      const failure = document.createElement("p");
      failure.className = "ovf-comments-status";
      failure.textContent = error.message || labels.errorLabel;
      container.appendChild(failure);
    }
  }

  if (form) {
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const textarea = document.getElementById("ovf-comment-content");
      const content = textarea.value.trim();
      if (!content) return;
      const submitButton = form.querySelector("button[type=submit]");
      if (submitButton) submitButton.disabled = true;
      try {
        await apiRequest(apiBase, {
          method: "POST",
          body: JSON.stringify({ content }),
        });
        textarea.value = "";
        if (formStatus) formStatus.textContent = "";
        await loadThread();
      } catch (error) {
        if (formStatus) formStatus.textContent = error.message || labels.errorLabel;
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    });
  }

  loadThread();
}

initComments();
